/**
 * BleedSkillEffect — Blades tree rend path Fork 2-A: instant max bleed stacks.
 *
 * Single lateral slash that applies 3 bleed stacks simultaneously, skipping
 * the buildup entirely. Long cooldown. Pre-checks existing bleed stacks on
 * target for The Debt bonus (direct hit damage scales with pre-existing
 * bleed count).
 *
 * Supports upgrade keys via resolver:
 * - The Debt: `damagePerExistingStack` (additive) — +10% hit damage per
 *   existing bleed stack at activation time
 * - Inevitability: resolved at tick time (cross-cutting), not in this class
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see blades-spec.md § Bleed
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BleedDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
    getStacks(effectId: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BleedConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly bleedIntensity: number;
  readonly stacksPerHit: number;
  readonly baseDamageMultiplier: number;
}

const DEFAULT_CONFIG: BleedConfig = {
  cooldownMs: 2000,                      // long cooldown tier
  baseDurationMs: 6000,                  // 6s bleed duration
  bleedIntensity: 3,                     // 3 damage per tick per stack
  stacksPerHit: 3,                       // always applies max stacks
  baseDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BleedSkillEffect implements SkillEffect {
  readonly skillId = 'bleed';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BleedDeps;
  private readonly config: BleedConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _bleedApplications = 0;
  private _preExistingStacks = 0;
  private _resolvedDamageMultiplier = 1.0;

  constructor(deps: BleedDeps, config: Partial<BleedConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get bleedApplications(): number { return this._bleedApplications; }
  get preExistingStacks(): number { return this._preExistingStacks; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Check pre-existing bleed stacks for The Debt bonus
    this._preExistingStacks = this.deps.statusEffects.getStacks('bleed');
    const debtBonusPerStack = this.deps.resolver.getAdditiveEffect('bleed', 'damagePerExistingStack');
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      (1 + this._preExistingStacks * debtBonusPerStack);

    // Fire the attack
    const enemiesHit = this.deps.combat.startAttack('bleed');
    this._attacksFired++;

    // Apply bleed stacks on hit
    if (enemiesHit > 0) {
      const bonusMs = this.deps.resolver.getAdditiveEffect('rend', 'bleedDurationBonusMs');
      this.deps.statusEffects.apply('bleed', {
        sourceSkillId: 'bleed',
        durationMs: this.config.baseDurationMs + bonusMs,
        intensity: this.config.bleedIntensity,
        stacks: this.config.stacksPerHit,
      });
      this._bleedApplications++;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
